<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "~/composables/useI18n";
import { useViewport } from "~/composables/useViewport";

const navItems = [
  {
    label: "Home",
    path: "/",
    ariaLabel: "Navigate to homepage",
    isEnabled: true,
  },
  {
    label: "About Me",
    path: "/#about-me",
    ariaLabel: "Navigate to About Me section",
    isEnabled: true,
  },
  {
    label: "Projects",
    path: "/projects",
    ariaLabel: "View projects portfolio",
    isEnabled: true,
  },
  {
    label: "Studies",
    path: "/studies",
    ariaLabel: "View education and courses",
    isEnabled: false,
  },
  {
    label: "Skills",
    path: "/skills",
    ariaLabel: "View skills catalog",
    isEnabled: true,
  },
  {
    label: "Hobbies",
    path: "/hobbies",
    ariaLabel: "View personal interests",
    isEnabled: false,
  },
  {
    label: "Lab",
    path: "/lab",
    ariaLabel: "Read blog posts and experiments",
    isEnabled: false,
  },
];

const enabledNavItems = navItems.filter((item) => item.isEnabled);

const t = useI18n();
const isMenuOpen = ref(false);
const navElement = ref<HTMLElement | null>(null);
const { isSmallScreen } = useViewport();
const showLogo = ref(false);
const isScrolled = ref(false);
const scrolledBgClassNames =
  "bg-primary-100 dark:bg-primary-800 shadow-[0_1px_10px_0_rgba(0,0,0,0.2)] dark:shadow-[0_1px_10px_0_rgba(0,0,0,0.3)]";
const notScrolledBgClassNames = "bg-white dark:bg-primary-950";
const borderClassNames = ["border-gray-200 dark:border-primary-900"];

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const handleScroll = () => {
  if (navElement.value) {
    const navHeight = navElement.value.offsetHeight;
    isScrolled.value = window.scrollY > navHeight;

    // Always show logo on small screens
    if (!isSmallScreen.value) {
      showLogo.value = window.scrollY > navHeight * 2;
    }
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
  handleScroll(); // Check initial state
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <nav
    ref="navElement"
    class="fixed top-0 left-0 right-0 z-50"
    aria-label="Main navigation"
  >
    <div :class="['lg:container lg:mx-auto']">
      <div
        class="flex items-center justify-between h-[var(--main-nav-height)] relative px-4 z-10"
        :class="[
          'transition-all duration-300 border-l border-r',
          borderClassNames,
          isSmallScreen || isScrolled
            ? scrolledBgClassNames
            : notScrolledBgClassNames,
        ]"
      >
        <!-- Logo/Brand -->
        <div
          :class="[
            'flex-shrink-0 transition-opacity duration-300',
            showLogo ? 'opacity-100' : 'opacity-0',
          ]"
        >
          <NuxtLink
            to="/"
            class="text-xl font-bold text-primary-700 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-400 block"
            aria-label="Go to homepage"
          >
            <slot name="title" />
          </NuxtLink>
        </div>

        <!-- Desktop Navigation -->
        <div class="hidden md:block h-full">
          <div class="flex items-center h-full space-x-0">
            <NuxtLink
              v-for="item in enabledNavItems"
              :key="item.path"
              :to="item.path"
              :aria-label="item.ariaLabel"
              class="h-full flex items-center px-4 text-sm font-medium bg-transparent border-transparent text-primary-800 hover:bg-primary-200 dark:bg-transparent dark:border-transparent dark:text-primary-200 dark:hover:bg-primary-900/50 transition-colors"
              active-class="bg-primary-200/80 border-primary-300 text-primary-800 dark:bg-primary-900 dark:border-primary-900 dark:text-primary-100"
            >
              {{ item.label }}
            </NuxtLink>
            <!-- Resume Button -->
            <div class="flex items-center h-full px-2">
              <UiButton
                to="/resume/download"
                target="_blank"
                variant="solid"
                color="primary"
                size="xs"
                aria-label="Open resume as printable PDF"
              >
                {{ t("mainNav.resumeButtonLabel") }}
              </UiButton>
            </div>
            <!-- Dark Mode Toggle -->
            <div class="flex items-center h-full px-2">
              <UiToggleDarkMode />
            </div>
          </div>
        </div>

        <!-- Mobile menu button -->
        <div class="md:hidden flex items-center space-x-2">
          <!-- Dark Mode Toggle -->
          <UiToggleDarkMode />
          <button
            type="button"
            class="inline-flex items-center justify-center p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            :aria-expanded="isMenuOpen"
            aria-label="Toggle navigation menu"
            @click="toggleMenu"
          >
            <span class="sr-only">Open main menu</span>
            <!-- Hamburger icon -->
            <UIcon
              v-if="!isMenuOpen"
              name="i-custom-menu-hamburger"
              :size="24"
              mode="svg"
              aria-hidden="true"
            />
            <!-- Close icon -->
            <UIcon
              v-else
              name="i-custom-close"
              :size="24"
              mode="svg"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div
      v-if="isMenuOpen"
      :class="[
        'relative md:hidden border-t',
        borderClassNames,
        'bg-primary-100 dark:bg-primary-800 shadow-[0_3px_3px_0px_rgba(0,0,0,0.2)] dark:shadow-[0_3px_3px_0px_rgba(0,0,0,0.3)]',
        'z-20',
      ]"
    >
      <div class="px-2 pt-2 space-y-1">
        <NuxtLink
          v-for="item in enabledNavItems"
          :key="item.path"
          :to="item.path"
          :aria-label="item.ariaLabel"
          class="block px-4 py-3 text-base font-medium bg-transparent border-transparent text-primary-800 hover:bg-primary-100 dark:bg-transparent dark:border-transparent dark:text-primary-200 dark:hover:bg-primary-900/50 transition-colors"
          active-class="bg-primary-200/80 border-primary-300 text-primary-800 dark:bg-primary-900 dark:border-primary-900 dark:text-primary-100"
          @click="closeMenu"
        >
          {{ item.label }}
        </NuxtLink>
        <!-- Resume Button (mobile) -->
        <UButton
          to="/resume/download"
          class="block px-8 -mx-4 py-3 text-base font-medium bg-primary-200 border-t border-primary-400 text-primary-800 dark:bg-primary-900 dark:border-primary-950 dark:text-primary-100 dark:hover:bg-primary-900/50 transition-colors"
          target="_blank"
          aria-label="Open resume as printable PDF"
          @click="closeMenu"
        >
          {{ t("mainNav.resumeButtonLabel") }}
        </UButton>
      </div>
    </div>
  </nav>
</template>
